import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { GetSimilarVehiclesResponseWithVersion } from 'Services/api/vehicles.types';
import { logger } from 'Services/logger/logger';
import type { EnquiryState } from 'Types/enquiryStates';
import type { Vehicle } from 'Types/vehicle';

import { similarVehiclesFetcher } from './useFetchSimilarVehicles.helpers';

interface UseFetchSimilarVehiclesProps {
	enquiryId: Vehicle['id'];
	isEnabled?: boolean;
	normalisedMake: string;
	normalisedModel: string;
	stateSlug: EnquiryState['slug'];
}

export const useFetchSimilarVehicles = ({
	enquiryId,
	isEnabled = true,
	normalisedMake,
	normalisedModel,
	stateSlug,
}: UseFetchSimilarVehiclesProps): UseQueryResult<GetSimilarVehiclesResponseWithVersion> => {
	return useQuery({
		enabled: isEnabled && Boolean(normalisedMake),
		onError: (error: AxiosError) => {
			const message = `There was an issue with fetching similar vehicles for vehicle: ${enquiryId}`;

			logger.error({
				context: { extra: { info: message } },
				error,
				message,
				scope: 'useFetchSimilarVehicles',
			});
		},
		queryFn: () => similarVehiclesFetcher({ enquiryId, make: normalisedMake, model: normalisedModel, stateSlug }),
		queryKey: ['similarVehicle', enquiryId],
	});
};
